export interface MaskRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MaskSize {
  width: number;
  height: number;
}

export interface MaskPercentages {
  x: string;
  y: string;
  width: string;
  height: string;
}

const minimumMaskSide = 8;

export function rectFromPoints(start: Pick<MaskRect, 'x' | 'y'>, end: Pick<MaskRect, 'x' | 'y'>): MaskRect {
  return { x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y) };
}

export function rectFromPercentages(values: MaskPercentages, size: MaskSize): MaskRect | null {
  if (Object.values(values).some((value) => value.trim() === '')) return null;
  const x = Number(values.x);
  const y = Number(values.y);
  const width = Number(values.width);
  const height = Number(values.height);
  if (![x, y, width, height].every(Number.isFinite) || x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 100 || y + height > 100) return null;
  return { x: size.width * x / 100, y: size.height * y / 100, width: size.width * width / 100, height: size.height * height / 100 };
}

export function normalizeMaskRect(rect: MaskRect, size: MaskSize): MaskRect {
  const x = Math.min(Math.max(rect.x, 0), size.width);
  const y = Math.min(Math.max(rect.y, 0), size.height);
  const right = Math.min(Math.max(rect.x + Math.max(rect.width, 0), 0), size.width);
  const bottom = Math.min(Math.max(rect.y + Math.max(rect.height, 0), 0), size.height);
  return { x, y, width: Math.max(0, right - x), height: Math.max(0, bottom - y) };
}

export function rectToPercentages(rect: MaskRect, size: MaskSize): MaskPercentages {
  return {
    x: String(Math.round(rect.x / size.width * 100)),
    y: String(Math.round(rect.y / size.height * 100)),
    width: String(Math.round(rect.width / size.width * 100)),
    height: String(Math.round(rect.height / size.height * 100)),
  };
}

function validatedMaskRect(current: MaskRect[], rect: MaskRect, size: MaskSize): { rects: MaskRect[]; rect: MaskRect | null; error: string | null } {
  const normalized = normalizeMaskRect(rect, size);
  if (normalized.width <= minimumMaskSide || normalized.height <= minimumMaskSide) return { rects: current, rect: null, error: '가릴 영역을 조금 더 크게 지정해 주세요.' };
  return { rects: current, rect: normalized, error: null };
}

export function appendMaskRect(current: MaskRect[], rect: MaskRect, size: MaskSize): { rects: MaskRect[]; error: string | null } {
  const result = validatedMaskRect(current, rect, size);
  return result.error ? { rects: current, error: result.error } : { rects: [...current, result.rect!], error: null };
}

export function replaceMaskRect(current: MaskRect[], index: number, rect: MaskRect, size: MaskSize): { rects: MaskRect[]; error: string | null } {
  const result = validatedMaskRect(current, rect, size);
  if (result.error || index < 0 || index >= current.length) return { rects: current, error: result.error ?? '수정할 가림 영역을 다시 골라 주세요.' };
  return { rects: current.map((item, currentIndex) => currentIndex === index ? result.rect! : item), error: null };
}
